
import React, { useState, useEffect } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, CartesianGrid } from 'recharts';
import { Wallet, TrendingUp, Activity, ChevronRight, Sparkles, ShieldAlert, Clock, PieChart, Scroll, Info, BookOpen } from 'lucide-react';
import { GoogleGenAI } from "@google/genai";

export const Dashboard = ({ metrics, wealthMetrics, onNavigate }: any) => {
  const [aiTip, setAiTip] = useState<string>("Analyzing your financial patterns...");
  const [loadingAi, setLoadingAi] = useState(true);
  const [activeHint, setActiveHint] = useState<string | null>(null);

  useEffect(() => {
    fetchAiInsight();
  }, [metrics.surplus, metrics.savingsRate, wealthMetrics.nav]);

  const fetchAiInsight = async () => {
    setLoadingAi(true);
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY });
      const prompt = `Analyze these financial metrics for a user: 
        Monthly Surplus: R${metrics.surplus}, 
        Savings Rate: ${metrics.savingsRate}%, 
        Net Worth: R${wealthMetrics.nav}, 
        Debt Ratio: ${wealthMetrics.debtRatio}%. 
        Provide a single, professional, high-impact financial tip in 15 words or less. Do not use generic advice. Focus on the next logical step for this specific profile.`;
      
      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: prompt,
      });
      setAiTip(response.text || "Continue optimizing your surplus for long-term growth.");
    } catch (e) {
      setAiTip("Surplus detected. Strategy: Optimize for compound growth.");
    } finally {
      setLoadingAi(false);
    }
  };

  const toggleHint = (id: string) => {
    if (activeHint === id) setActiveHint(null);
    else setActiveHint(id);
  };

  return (
    <div className="space-y-8 animate-fade-in" onClick={() => setActiveHint(null)}>
      {/* AI Insights Card */}
      <div className="bg-emerald/5 border border-emerald/20 rounded-3xl p-6 flex items-start gap-5 group hover:bg-emerald/10 transition-colors relative">
        <div className="p-3 bg-emerald rounded-2xl shadow-lg shadow-emerald/20 text-white shrink-0">
          <Sparkles size={20} className={loadingAi ? "animate-pulse" : ""} />
        </div>
        <div className="flex-grow">
          <div className="flex justify-between items-center mb-1">
            <h4 className="text-[10px] font-inter font-black text-emerald uppercase tracking-[0.2em]">AI Financial Intelligence</h4>
            <div className="flex items-center gap-2">
              <button 
                onClick={(e) => { e.stopPropagation(); fetchAiInsight(); }}
                className={`text-emerald/40 hover:text-emerald transition-colors ${loadingAi ? 'animate-spin' : ''}`}
                disabled={loadingAi}
                title="Refresh Tip"
              >
                <Clock size={14} />
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); toggleHint('ai-intelligence'); }}
                className="text-emerald/40 hover:text-emerald transition-colors"
              >
                <Info size={14} />
              </button>
            </div>
          </div>
          <p className="text-navy text-sm font-semibold leading-relaxed">
            {loadingAi ? "Synthesizing market data..." : aiTip}
          </p>
          
          {activeHint === 'ai-intelligence' && (
            <div className="absolute top-12 right-6 w-48 bg-navy text-white text-[10px] p-4 rounded-2xl shadow-xl z-50 animate-fade-in leading-relaxed">
              This insight is generated using Gemini 3 Flash, analyzing your real-time surplus and debt ratios.
            </div>
          )}
        </div>
      </div>

      {/* User Manual Quick Access */}
      <div 
        onClick={() => onNavigate('manual')}
        className="bg-navy rounded-3xl p-6 flex items-center justify-between gap-6 group cursor-pointer hover:bg-navy/90 transition-all border border-white/5 shadow-xl"
      >
        <div className="flex items-center gap-5">
          <div className="p-4 bg-white/10 rounded-2xl text-electric group-hover:scale-110 transition-transform">
            <BookOpen size={24} />
          </div>
          <div>
            <h4 className="text-white font-bold text-lg leading-tight">New to StackUp?</h4>
            <p className="text-slate-400 text-sm">Read our simple guide on how to get started and navigate the app.</p>
          </div>
        </div>
        <div className="p-3 bg-white/5 rounded-xl text-white/40 group-hover:text-white group-hover:bg-electric transition-all">
          <ChevronRight size={20} />
        </div>
      </div>

      <div className="bg-navy p-6 md:p-10 rounded-4xl shadow-2xl shadow-navy/20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-electric/10 rounded-full blur-3xl -mr-20 -mt-20"></div>
        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6 md:gap-8">
          <div className="space-y-1">
            <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">Capital Overview</h2>
            <p className="text-sky font-medium opacity-80 uppercase text-[10px] tracking-widest">Global Snapshot</p>
          </div>
          <div className="flex items-center justify-between md:justify-end gap-6 md:gap-10">
            <div className="text-left md:text-right relative">
              <div className="flex items-center md:justify-end gap-1 mb-1 opacity-60">
                <button 
                  onClick={(e) => { e.stopPropagation(); toggleHint('surplus'); }}
                  className="text-sky hover:text-white transition-colors"
                >
                  <Info size={10} />
                </button>
                <div className="text-[10px] text-sky font-bold uppercase tracking-widest">Surplus</div>
              </div>
              <div className={`text-3xl md:text-4xl font-extrabold tracking-tighter text-white`}>
                R{metrics.surplus.toLocaleString()}
              </div>
              {activeHint === 'surplus' && (
                <div className="absolute top-full mt-2 right-0 w-48 bg-navy text-white text-[10px] p-4 rounded-2xl shadow-2xl z-[100] animate-fade-in leading-relaxed border border-white/10 text-left">
                  <div className="font-black mb-1 uppercase tracking-widest text-electric">Surplus</div>
                  The amount of money left over after all expenses are paid this month. (Income - Expenses).
                </div>
              )}
            </div>
            <div className="text-left md:text-right relative">
              <div className="flex items-center md:justify-end gap-1 mb-1 opacity-60">
                <button 
                  onClick={(e) => { e.stopPropagation(); toggleHint('wealth-score'); }}
                  className="text-sky hover:text-white transition-colors"
                >
                  <Info size={10} />
                </button>
                <div className="text-[10px] font-inter text-sky font-bold uppercase tracking-widest">Wealth Score</div>
              </div>
              <div className="text-3xl md:text-4xl font-extrabold text-emerald tracking-tighter">{metrics.score}</div>
              {activeHint === 'wealth-score' && (
                <div className="absolute top-full mt-2 right-0 w-48 bg-navy text-white text-[10px] p-4 rounded-2xl shadow-2xl z-[100] animate-fade-in leading-relaxed border border-white/10 text-left">
                  <div className="font-black mb-1 uppercase tracking-widest text-emerald">Wealth Score</div>
                  A rating from 0-100 based on your savings consistency and debt management.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard 
          label="Liquidity" 
          value={`R${wealthMetrics.liquidity.toLocaleString()}`} 
          sub="Cash on Hand" 
          icon={<Wallet className="text-electric" size={20} />} 
          trend="Immediate Access"
          hint="Significance: Your financial safety net for emergencies. Calculation: Sum of all cash and bank balances available within 24 hours."
          activeHint={activeHint}
          onHintToggle={(e: any) => { e.stopPropagation(); toggleHint('liquidity'); }}
          hintId="liquidity"
        />
        <StatCard 
          label="Retention" 
          value={`${metrics.savingsRate}%`} 
          sub="Savings Velocity" 
          color="text-navy"
          icon={<TrendingUp className="text-electric" size={20} />} 
          trend="Flow efficiency"
          hint="Significance: The primary driver of wealth building. Calculation: (Monthly Surplus / Total Monthly Income) × 100."
          activeHint={activeHint}
          onHintToggle={(e: any) => { e.stopPropagation(); toggleHint('retention'); }}
          hintId="retention"
        />
        <StatCard 
          label="Independence" 
          value={`${wealthMetrics.freedomProgress.toFixed(1)}%`} 
          sub="Freedom Goal" 
          icon={<Activity className="text-sky" size={20} />} 
          trend="Journey to summit"
          hint="Significance: Progress toward living entirely off your assets. Calculation: Net Worth / (Annual Expenses × 25)."
          activeHint={activeHint}
          onHintToggle={(e: any) => { e.stopPropagation(); toggleHint('freedom'); }}
          hintId="freedom"
        />
      </div>

      {/* Power Tools Grid */}
      <div className="space-y-4">
        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-2 flex items-center gap-2">
          Power Tools
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <ToolButton icon={<ShieldAlert size={20} />} label="Bunker" sub="Emergency" color="text-rose-500" onClick={() => onNavigate('emergency')} />
          <ToolButton icon={<Clock size={20} />} label="Simulator" sub="Compound" color="text-electric" onClick={() => onNavigate('investment')} />
          <ToolButton icon={<PieChart size={20} />} label="Analyzer" sub="Portfolio" color="text-sky" onClick={() => onNavigate('portfolio')} />
          <ToolButton icon={<Scroll size={20} />} label="Legacy" sub="Planning" color="text-emerald-500" onClick={() => onNavigate('legacy')} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="fin-card p-8 rounded-3xl min-w-0 relative">
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-2">
              <h3 className="text-xs font-extrabold text-slate-400 uppercase tracking-widest">Budget Distribution</h3>
              <button 
                onClick={(e) => { e.stopPropagation(); toggleHint('budget-dist'); }}
                className="text-slate-300 hover:text-electric transition-colors"
              >
                <Info size={12} />
              </button>
            </div>
            <button onClick={() => onNavigate('planner')} className="text-xs font-bold text-electric hover:underline flex items-center gap-1">
              Adjust <ChevronRight size={14} />
            </button>
          </div>
          {activeHint === 'budget-dist' && (
            <div className="absolute top-16 left-8 right-8 bg-navy text-white text-[10px] p-4 rounded-2xl shadow-2xl z-50 animate-fade-in border border-white/10">
              <div className="font-black mb-1 uppercase tracking-widest text-electric">Budget Distribution</div>
              Shows how your actual spending compares to the limits you set in the Planner. Significance: Helps identify overspending categories.
            </div>
          )}
          <div className="space-y-6">
            {metrics.categories.map((cat: any) => {
              const percent = Math.round((cat.actual / cat.limit) * 100);
              const isOver = percent > 100;
              return (
                <div key={cat.label} className="group">
                  <div className="flex justify-between items-end mb-2">
                    <div className="flex flex-col">
                      <span className="text-sm font-bold text-navy">{cat.label}</span>
                      <span className="text-[10px] font-bold text-slate-400">R{cat.actual.toLocaleString()} spent</span>
                    </div>
                    <span className={`text-xs font-bold ${isOver ? 'text-rose-500' : 'text-navy'}`}>
                      {percent}%
                    </span>
                  </div>
                  <div className="h-2.5 bg-slate-50 rounded-full overflow-hidden border border-slate-100">
                    <div className={`h-full transition-all duration-1000 ease-in-out ${isOver ? 'bg-rose-500' : 'bg-electric'}`} style={{ width: `${Math.min(100, percent)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="fin-card p-8 rounded-3xl flex flex-col min-w-0 relative">
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-2">
              <h3 className="text-xs font-extrabold text-slate-400 uppercase tracking-widest">Capital Composition</h3>
              <button 
                onClick={(e) => { e.stopPropagation(); toggleHint('capital-comp'); }}
                className="text-slate-300 hover:text-electric transition-colors"
              >
                <Info size={12} />
              </button>
            </div>
            <button onClick={() => onNavigate('wealth')} className="text-xs font-bold text-electric hover:underline flex items-center gap-1">
              Explore <ChevronRight size={14} />
            </button>
          </div>
          {activeHint === 'capital-comp' && (
            <div className="absolute top-16 left-8 right-8 bg-navy text-white text-[10px] p-4 rounded-2xl shadow-2xl z-50 animate-fade-in border border-white/10">
              <div className="font-black mb-1 uppercase tracking-widest text-electric">Capital Composition</div>
              A visual breakdown of your spending by category. Significance: Highlights where the majority of your capital is being deployed.
            </div>
          )}
          <div className="relative w-full h-[260px] min-h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={metrics.categories}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="label" stroke="#94A3B8" fontSize={10} axisLine={false} tickLine={false} dy={10} />
                <YAxis hide />
                <Tooltip cursor={{fill: '#F8FAFC'}} contentStyle={{backgroundColor: '#fff', border: '1px solid #F1F5F9', borderRadius: '12px', fontSize: '12px'}} />
                <Bar dataKey="actual" radius={[4, 4, 4, 4]} barSize={32}>
                  {metrics.categories.map((entry: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* AI Financial Coach Section */}
      <div className="bg-white border border-slate-100 rounded-4xl p-8 shadow-sm relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-32 h-32 bg-emerald/5 rounded-full -mr-16 -mt-16 group-hover:bg-emerald/10 transition-colors"></div>
        <div className="relative">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-emerald/10 rounded-xl text-emerald">
              <Sparkles size={20} />
            </div>
            <h3 className="text-lg font-black text-navy">AI Financial Coach</h3>
          </div>
          
          <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
            <p className="text-slate-600 text-sm leading-relaxed italic">
              "{loadingAi ? "Your coach is reviewing your portfolio..." : aiTip}"
            </p>
          </div>
          
          <div className="mt-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
              <span className="text-[10px] font-inter font-bold text-slate-400 uppercase tracking-widest">Live Analysis Active</span>
            </div>
            <button 
              onClick={fetchAiInsight}
              disabled={loadingAi}
              className="text-xs font-bold text-emerald hover:text-navy transition-colors flex items-center gap-2"
            >
              {loadingAi ? "Analyzing..." : "Get New Insight"}
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ToolButton = ({ icon, label, sub, color, onClick }: any) => (
  <button onClick={onClick} className="fin-card p-5 rounded-3xl hover:border-electric/30 transition-all text-left group active:scale-95">
    <div className={`mb-3 ${color} group-hover:scale-110 transition-transform`}>{icon}</div>
    <div className="text-xs font-black text-navy leading-none mb-1">{label}</div>
    <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{sub}</div>
  </button>
);

const StatCard = ({ label, value, sub, icon, color = "text-navy", trend, hint, activeHint, onHintToggle, hintId }: any) => (
  <div className="fin-card p-8 rounded-3xl hover:border-emerald/30 transition-all group relative active:scale-[0.99]">
    <div className="flex justify-between items-start mb-6">
       <div className="p-3 bg-slate-50 rounded-2xl group-hover:bg-emerald/10 transition-colors">
         {icon}
       </div>
       <button 
         onClick={onHintToggle}
         className="text-slate-200 hover:text-emerald transition-colors focus:outline-none"
       >
         <Info size={14} />
       </button>
    </div>
    <div className="space-y-1">
      <div className="text-[10px] font-inter text-slate-400 font-bold uppercase tracking-widest">{label}</div>
      <div className={`text-2xl font-extrabold tracking-tight ${color}`}>{value}</div>
      <div className="flex items-center gap-2 mt-2">
        <span className="text-[10px] font-inter text-slate-500 font-bold">{sub}</span>
        <span className="w-1 h-1 bg-slate-200 rounded-full"></span>
        <span className="text-[10px] font-inter text-slate-400 font-medium italic">{trend}</span>
      </div>
    </div>

    {activeHint === hintId && (
      <div className="absolute top-14 left-6 right-6 bg-navy text-white text-[10px] p-5 rounded-2xl shadow-2xl z-50 animate-fade-in leading-relaxed border border-white/10">
        <div className="font-black mb-2 uppercase tracking-[0.2em] text-emerald">Command Intelligence</div>
        {hint}
      </div>
    )}
  </div>
);
